import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { getSettingString, getSettingEnum } from "../../../models/utilities/settingUtilities";
import { QuestionModel } from "../../../models/QuestionModel";
import useStyle from "./ImageChoiceRenderer.styles";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { LabelPosition } from "../../../models/enums/LabelPosition";
import { QuestionType } from "../../../models/enums/QuestionType";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForInputFieldQuestion,
} from "../../../utils/WCAGHelper";

interface Props extends RendererProps {
    onSelect?(): void;
    selected?: boolean;
    gridRendering?: boolean;
    choice?: ChoiceModel;
    disabled?: boolean;
}

function getImageDimensions(question: QuestionModel) {
    const parentWidth = getSettingString(question.parent, "ImageWidth");
    const width = getSettingString(question, "ImageWidth", parentWidth);

    const parentHeight = getSettingString(question.parent, "ImageHeight");
    const height = getSettingString(question, "ImageHeight", parentHeight);

    return {
        width,
        height,
    };
}

function displayChoiceLabels(question: QuestionModel, gridRendering: boolean) {
    const showChoiceText = getSettingEnum(question, LabelPosition, "ShowChoiceText");
    return showChoiceText != null && showChoiceText !== LabelPosition.No && !gridRendering;
}

export default function ImageChoiceRenderer(props: Props) {
    const {
        choice,
        onSelect,
        gridRendering,
        selected,
        disabled,
        gridRenderedColumnIndex,
        gridRenderedRowIndex,
    } = props;

    const theme = useTheme();
    const classes = useStyle({ theme, selected });
    const divRef = React.useRef();
    const question = useQuestion();
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;

    const choiceText = useTranslatable(choice, "TEXT");
    const imageSrc = useTranslatable(choice, "ImageUrl");

    const [active, setActive] = React.useState(false);

    const inputId = `${choice.elementId}_INPUT`;
    const labelId = `${choice.elementId}_LABEL`;
    const displayChoiceLabel = displayChoiceLabels(question, gridRendering);
    const showLabelText = displayChoiceLabel || !imageSrc;
    const showTabIndexIfTextOnlyForImage = !imageSrc && showLabelText;

    const collapsedGrid = gridRendering === false;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const useAriaLabelledBy = useAriaLabelledByForInputFieldQuestion(question);
    const ariaAttributes = {};
    const isChoiceOrHybridGridType =
        questionType === QuestionType.ChoiceGrid ||
        questionType === QuestionType.ImageChoiceGrid ||
        questionType === QuestionType.HybridGrid;

    // For hybrid grid both choice and column will exist
    const choiceOrColumnHasIndexExist = gridRenderedColumnIndex || (choice && choice.index >= 0);

    if (useAriaLabelledBy && isChoiceOrHybridGridType) {
        if (choiceOrColumnHasIndexExist) {
            ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
                question,
                questionId,
                `${questionType.toString()}_CHOICEOPTION`,
                (gridRenderedColumnIndex ?? 0) + 1,
                gridRenderedRowIndex + 1,
                (choice.index ?? 0) + 1, // In grid question we have sub-question type so required column level index and choice level index
            );
        }
    } else {
        const choiceAriaText = choiceText;
        if (choiceAriaText) {
            ariaAttributes["aria-label"] = choiceAriaText;
        }
    }

    return (
        <div
            className={classnames(
                classes.imageChoice,
                collapsedGrid && classes.imageChoiceCollapsed,
                disabled && classes.imageDisabled,
            )}
        >
            {imageSrc && (
                <div
                    id={inputId}
                    className={classnames(
                        classes.imageChoiceTarget,
                        disabled && classes.imageDisabled,
                    )}
                    ref={divRef}
                    role="checkbox"
                    aria-checked={selected}
                    {...ariaAttributes}
                    tabIndex={0}
                    style={{ ...getImageDimensions(question) }}
                    onMouseDown={disabled ? undefined : (event) => event.preventDefault()}
                    onFocus={disabled ? undefined : () => setActive(true)}
                    onBlur={disabled ? undefined : () => setActive(false)}
                    onMouseOver={disabled ? undefined : () => setActive(true)}
                    onMouseOut={disabled ? undefined : () => setActive(false)}
                    onClick={disabled ? undefined : onSelect}
                    onKeyDown={
                        disabled
                            ? undefined
                            : (event) => {
                                  if (event.target !== divRef.current) return;
                                  if (event.key === " ") {
                                      onSelect();
                                      event.preventDefault();
                                  }
                              }
                    }
                >
                    <div
                        className={classnames(
                            classes.imageChoiceOverlay,
                            active && classes.imageChoiceOverlayActive,
                            selected && classes.imageChoiceOverlaySelected,
                            disabled && classes.imageDisabled,
                        )}
                    />
                    <img
                        className={classnames(
                            classes.imageChoiceImage,
                            disabled && classes.imageDisabled,
                        )}
                        src={imageSrc}
                        alt={choiceText}
                        data-selected={String(selected)}
                    />
                </div>
            )}

            {showLabelText && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                    id={labelId}
                    className={classnames(
                        classes.imageChoiceText,
                        collapsedGrid && classes.imageChoiceCollapsedText,
                        selected && classes.imageChoiceTextSelected,
                        disabled && classes.imageDisabled,
                    )}
                    onMouseOver={disabled ? undefined : () => setActive(true)}
                    onMouseOut={disabled ? undefined : () => setActive(false)}
                    {...(showTabIndexIfTextOnlyForImage ? { ...ariaAttributes, tabIndex: 0 } : {})}
                    onClick={
                        disabled
                            ? undefined
                            : () => {
                                  onSelect();
                                  event.preventDefault();
                              }
                    }
                    onMouseDown={(event) => event.preventDefault()}
                    dangerouslySetInnerHTML={{ __html: choiceText }}
                />
            )}
        </div>
    );
}
